import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { IGetCodes } from 'src/app/interfaces/codes';
import { ContentManagementService } from 'src/app/Services/content-management/content-management.service';

@Component({
  selector: 'app-question-multiple-assign',
  templateUrl: './question-multiple-assign.component.html',
  styleUrls: ['./question-multiple-assign.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class QuestionMultipleAssignComponent implements OnInit {
  @Input() courseCategoryList: IGetCodes[] = [];
  @Input() initialSelection: any;
  @Output() selectionChanged = new EventEmitter<any>();

  form: FormGroup;
  selectedCategory: string | null = null; // Track the selected category code

  constructor(
    private fb: FormBuilder,
    private service: ContentManagementService,
    private toaster: ToastrService,
    public activeModal: NgbActiveModal,
  ) {}

  async ngOnInit(): Promise<void> {
    this.form = this.fb.group({
      courseCategoryType: this.fb.array([]),
      applicableCourses: this.fb.array([]),
    });

    this.makeCourseCategoryTypeArray();

    if (this.initialSelection) {
      this.selectedCategory = this.initialSelection.selectedCategory;
      const category = this.getCategoryByCode(this.selectedCategory??'');
      const courses = this.initialSelection.courses;

      for (const c of courses) {
        const [examAgencyList, courseList, courseYear] = await Promise.all([
          this.service.GetCode({ Tablename: `EXAM_AGENCY@${this.selectedCategory}` }).toPromise(),
          this.service.GetCode({ Tablename: `EXAM_COURSE@${c.examAgency}@Course_Category_Type@${this.selectedCategory}` }).toPromise(),
          this.service.GetCode({ Tablename: `COURSE_YEAR@${c.courseId}@${this.selectedCategory}` }).toPromise(),
        ]);

        this.applicableCoursesArray.push(
          this.fb.group({
            courseId: [c.courseId, Validators.required],
            examAgency: [c.examAgency, Validators.required],
            examYear: [c.examYear, Validators.required],
            deleteInd: ['N'],
            courseList: [courseList || []],
            courseYear: [courseYear || []],
            examAgencyList: [examAgencyList || []],
            type: [this.selectedCategory],
          })
        );
      }
    }
  }


  makeCourseCategoryTypeArray() {
    // Initialize as an empty array for compatibility
    const courseCategoryArray = this.form.get('courseCategoryType') as FormArray;
    this.courseCategoryList.forEach((category) => {
      courseCategoryArray.push(
        this.fb.group({
          CourseCategoryTypeCode: category.Code,
          CourseCategoryTypeName: category.Description,
          // No SelectedInd needed for radios
        })
      );
    });
  }

  get courseCategoryTypeArray() {
    return this.form.get('courseCategoryType') as FormArray;
  }

  get applicableCoursesArray() {
    return this.form.get('applicableCourses') as FormArray;
  }

  // Called when user selects a category (radio)
  onCategoryTypeChange(code: string) {
    if (this.selectedCategory !== code) {
      // Remove any courses not matching the new category
      for (let i = this.applicableCoursesArray.length - 1; i >= 0; i--) {
        if (this.applicableCoursesArray.at(i).value.type !== code) {
          this.applicableCoursesArray.removeAt(i);
        }
      }
      this.selectedCategory = code;
    this.addApplicableCourses(this.getCategoryByCode(this.selectedCategory))

    }
  }

  addApplicableCourses(category: IGetCodes) {
    if (!this.selectedCategory || this.selectedCategory !== category.Code) return;

    this.service
      .GetCode({ Tablename: `EXAM_AGENCY@${category.Code}` })
      .subscribe((examAgencyList) => {
        this.applicableCoursesArray.push(
          this.fb.group({
            courseId: ['', Validators.required],
            examAgency: ['', Validators.required],
            examYear: ['', Validators.required],
            deleteInd: ['N'],
            courseList: [[]],
            courseYear: [[]],
            type: [category.Code],
            examAgencyList: [examAgencyList],
          })
        );
      });
  }

  removeCourse(ctrl: AbstractControl) {
    const idx = this.applicableCoursesArray.controls.indexOf(ctrl);
    if (idx !== -1) {
      this.applicableCoursesArray.removeAt(idx);
    }
  }

  fetchCourseBasedOnExam(appInstance: any, item: IGetCodes | undefined) {
    if (!item) return;
    this.service
      .GetCode({
        Tablename: `EXAM_COURSE@${appInstance.value.examAgency}@Course_Category_Type@${item.Code}`,
      })
      .subscribe((elem) => {
        appInstance.get('courseList').setValue(elem || []);
        appInstance.get('courseYear').setValue([]);
        appInstance.get('courseId').setValue('');
        appInstance.get('examYear').reset();
      });
  }

  fetchCourseYear(appExam: any, item: IGetCodes | undefined) {
    if (!item) return;
    this.service
      .GetCode({
        Tablename: `COURSE_YEAR@${appExam.value.courseId}@${item.Code}`,
      })
      .subscribe((elem) => {
        appExam.get('courseYear').setValue(elem || []);
        appExam.get('examYear').reset();
      });
  }

  getCategoryNameByCode(code: string): string {
    const found = this.courseCategoryList.find(x => x.Code === code);
    return found ? found.Description : '';
  }

  getCategoryByCode(code: string): IGetCodes {
    const found = this.courseCategoryList.find(x => x.Code === code);
    if (!found) {
      throw new Error('Category not found: ' + code);
    }
    return found;
  }


  getCoursesByCategory(categoryCode: string) {
    return this.applicableCoursesArray.controls.filter(
      ctrl => ctrl.value.type === categoryCode
    );
  }

  /**
   * Prevent duplicate course year in a single category.
   */
  onCourseYearChange(appExam: AbstractControl, category: IGetCodes) {
    const selectedYear = appExam.get('examYear')?.value;
    const typeCode = category.Code;

    // Find all courses in this category except the current one
    const sameCategoryCourses = this.getCoursesByCategory(typeCode)
      .filter(ctrl => ctrl !== appExam);

    // Check if any have the same course year
    const isDuplicate = sameCategoryCourses.some(ctrl =>
      ctrl.get('examYear')?.value === selectedYear
    );

    if (isDuplicate && selectedYear) {
      appExam.get('examYear')?.setValue(null);
      this.toaster.error(
        'You cannot select the same Course Year for more than one course within a category.',
        'Duplicate Course Year'
      );
    }
  }

  onSave() {
    if (!this.selectedCategory) {
      this.toaster.error('Please select a course category.', 'Validation Error');
      return;
    }
    if (this.form.valid) {
      // Only include the selected category's courses in the result
      const result = {
        selectedCategory: this.selectedCategory,
        selectedCategoryName: this.getCategoryNameByCode(this.selectedCategory),
        courses: this.getCoursesByCategory(this.selectedCategory).map(ctrl => {
          const value = ctrl.value;

          const courseName = (value.courseList || []).find((x: any) => x.Code === value.courseId)?.Description || '';
          const agencyName = (value.examAgencyList || []).find((x: any) => x.Code === value.examAgency)?.Description || '';
          const yearName = (value.courseYear || []).find((x: any) => x.Code === value.examYear)?.Description || '';

          return {
            courseId: value.courseId,
            courseName,
            examAgency: value.examAgency,
            examAgencyName: agencyName,
            examYear: value.examYear,
            examYearName: yearName,
            deleteInd: value.deleteInd,
            type: value.type,
          };
        }),
      };
      console.log('Selected Course Category & Courses:', result);
      this.activeModal.close(result);
    } else {
      this.form.markAllAsTouched();
      this.toaster.error('Please fill all required fields.', 'Validation Error');
    }
  }

  onCross() {
    this.activeModal.close(false);
  }
}
